/**
 * Subscribe-to-book interstitial (VOY-475).
 *
 * Mid-results CTA shown to non-subscribers between result cards. Copy
 * comes from the revenue spec (VOY-482): Basic $29.99/yr, billed annually (VOY-581).
 *
 * Self-gates on subscription state: returns null for subscribers, so
 * callers can drop it inline without a wrapping `!isSubscriber` check.
 * This is also why we replaced the inline `SubscribeCTABanner` inside
 * `FlightTabContent` with this component — single source of truth for
 * the mid-results CTA across the unified search surface.
 */
import { Link } from 'react-router-dom'
import { useSubscriptionRulesSyncState } from '@/hooks/useSubscriptionRules'

export interface SubscribeToBookInterstitialProps {
  /** Where the See-plans CTA links to. Defaults to /subscription. */
  href?: string
}

export function SubscribeToBookInterstitial({
  href = '/subscription',
}: SubscribeToBookInterstitialProps) {
  // Match the gating used by FlightTabContent's old inline banner:
  // `canHaveSpecialPrice` is true for paid tiers — those users already
  // see member prices, so hiding the upsell removes a confusing CTA.
  // VOY-512: no product scope on purpose. This CTA renders across BOTH flight and hotel
  // results (single source of truth for the mid-results upsell), and it only reads
  // `canHaveSpecialPrice` — a tier-derived flag independent of any product markup. So the
  // default 'all' scope is correct here; there is no product-specific value to resolve.
  // VOY-610: hidden too while the plan is unknown (loading or lookup failed), so a paying user
  // never sees it.
  const { rules, isPlanKnown } = useSubscriptionRulesSyncState()
  if (!isPlanKnown || rules.canHaveSpecialPrice) return null
  return (
    <div className="sr-subscribe" role="complementary" aria-label="Subscribe to Voy member pricing">
      <div className="sr-subscribe-copy">
        <h4>Subscribe to book at member prices</h4>
        <p>From $29.99/yr (Basic). Billed annually.</p>
      </div>
      <Link
        to={href}
        onClick={(e) => e.stopPropagation()}
        aria-label="See subscription plans"
        className="sr-subscribe-cta"
      >
        See plans
      </Link>
    </div>
  )
}
